// Chat REST service — REST is the source of truth; the WebSocket layer is a
// best-effort live mirror (see chatSocket.ts).

import { apiClient } from "@/services/apiClient";
import { env } from "@/config/env";
import type {
  ContactHrRequest,
  CreateThreadRequest,
  EnterpriseRecipient,
  HrRecipient,
  MessagePage,
  MessageResponse,
  SendMessageRequest,
  StudentRecipient,
  ThreadPage,
  ThreadResponse,
} from "../types";

/** One page is plenty for a chat sidebar; the backend default is only 20 */
const THREADS_PAGE_SIZE = 100;

export const chatService = {
  /** Backend returns a Spring Page — unwrap to the thread array */
  getThreads: async (): Promise<ThreadResponse[]> => {
    const page = await apiClient.get<ThreadPage>("/chat/threads", { size: THREADS_PAGE_SIZE });
    return page.content;
  },

  getThread: (id: string) => apiClient.get<ThreadResponse>(`/chat/threads/${id}`),

  /**
   * Approved enterprises as chat recipients (HR-only endpoint). The chat
   * participant id is each enterprise's owner.id (recruiter user id).
   */
  getEnterpriseRecipients: async (): Promise<EnterpriseRecipient[]> => {
    const page = await apiClient.get<{ content: EnterpriseRecipient[] }>("/enterprises", {
      status: "APPROVED",
      size: 100,
    });
    return page.content;
  },

  /** Requires CHAT_MODERATE (HR/Admin) */
  createThread: (data: CreateThreadRequest) =>
    apiClient.post<ThreadResponse>("/chat/threads", data),

  /** Active HR staff a student/enterprise can start a 1:1 with (requires CHAT_INITIATE) */
  getHrRecipients: async (): Promise<HrRecipient[]> =>
    apiClient.get<HrRecipient[]>("/chat/hr-recipients"),

  /** Students HR can start a thread with (HR-only, CHAT_MODERATE) */
  getStudentRecipients: async (): Promise<StudentRecipient[]> =>
    apiClient.get<StudentRecipient[]>("/chat/student-recipients"),

  /**
   * Student/enterprise entry point (requires CHAT_INITIATE): opens — or reuses
   * — a 1:1 conversation with the chosen HR. The optional message becomes the
   * first message.
   */
  contactHr: (data: ContactHrRequest) =>
    apiClient.post<ThreadResponse>("/chat/threads/contact-hr", data),

  markRead: (id: string) => apiClient.post<ThreadResponse>(`/chat/threads/${id}/read`),

  /** Requires CHAT_MODERATE */
  archive: (id: string) => apiClient.post<undefined>(`/chat/threads/${id}/archive`),

  /** Requires CHAT_MODERATE */
  unarchive: (id: string) => apiClient.post<undefined>(`/chat/threads/${id}/unarchive`),

  /**
   * Keyset pagination, walking FORWARD in time: no cursor = oldest slice,
   * pass the previous page's nextCursor as `since` for the next-newer slice.
   */
  getMessages: (threadId: string, params: { since?: string; size?: number }) =>
    apiClient.get<MessagePage>(`/chat/threads/${threadId}/messages`, params),

  sendMessage: (threadId: string, data: SendMessageRequest) =>
    apiClient.post<MessageResponse>(`/chat/threads/${threadId}/messages`, data),

  /**
   * Multipart variant: send a message carrying a file (WhatsApp-style).
   * Bypasses apiClient because it hardcodes a JSON Content-Type; the browser
   * must set the multipart boundary itself.
   */
  sendMessageWithFile: async (
    threadId: string,
    data: { content?: string; clientMessageId: string; file: File }
  ): Promise<MessageResponse> => {
    const token = localStorage.getItem("tr_access_token");
    const form = new FormData();
    if (data.content) form.append("content", data.content);
    form.append("clientMessageId", data.clientMessageId);
    form.append("file", data.file);

    const response = await fetch(`${env.VITE_API_URL}/chat/threads/${threadId}/messages`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
      body: form,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: { message?: string };
        message?: string;
      };
      throw new Error(body.error?.message ?? body.message ?? "Failed to send file");
    }
    const result = (await response.json()) as { data: MessageResponse };
    return result.data;
  },

  /** Download an attachment's bytes (raw body, not the JSON envelope) */
  downloadAttachment: async (attachmentId: string, fileName: string): Promise<void> => {
    const token = localStorage.getItem("tr_access_token");
    const response = await fetch(`${env.VITE_API_URL}/attachments/${attachmentId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to download file");
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  editMessage: (messageId: string, content: string) =>
    apiClient.patch<MessageResponse>(`/chat/messages/${messageId}`, { content }),

  deleteMessage: (messageId: string) => apiClient.delete<undefined>(`/chat/messages/${messageId}`),
};
